/**
 * 학원 상세 — 둘러보기(비수강) 공개 페이지
 * 소개·위치·요약·학원비·소식·영상·리뷰·주변학원 한 페이지 스크롤 + [전화/채팅 상담]
 * (가입한 학원은 MemberAcademyPage로 진입한다)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoCallOutline, IoClose, IoBookmark, IoCalendarOutline } from 'react-icons/io5';
import academyService from '../../services/academyService';
import AcademyPublicInfo from '../../components/academy/AcademyPublicInfo';
import BrandLoader from '../../components/common/BrandLoader';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';

const AcademyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { operatedAcademies } = useAcademy();

  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  // 본인이 운영하는 학원이면 수정 버튼 노출
  const isOwner =
    (!!userData?.uid && academy?.ownerId === userData.uid) ||
    (operatedAcademies || []).some((a) => a.id === id);

  const load = useCallback(async () => {
    const a = await academyService.getAcademy(id);
    setAcademy(a);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleCall = () => {
    if (academy?.phone) window.location.href = `tel:${academy.phone}`;
    else alert('전화번호가 준비 중이에요.');
  };

  if (loading) return <BrandLoader fullscreen />;
  if (!academy) return <Center>학원을 찾을 수 없어요.</Center>;

  return (
    <Wrap>
      {(() => {
        const gallery = (academy.promoPhotos || [])
          .map((p) => (typeof p === 'string' ? { url: p } : p))
          .filter((p) => p?.url);
        if (gallery.length === 0 && academy.photoUrl) gallery.push({ url: academy.photoUrl });
        const tiles = gallery.slice(0, 3);
        const extra = Math.max(0, gallery.length - 3);
        const openGallery = () => navigate(`/app/academy/${id}/photos`);
        const closeBtn = userData?.provider !== 'demo' && (
          <CloseBtn
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            aria-label="닫기"
          >
            <IoClose size={22} />
          </CloseBtn>
        );
        if (tiles.length === 0) {
          return (
            <Hero $url="">
              <HeroLetter>{academy.name.slice(0, 1)}</HeroLetter>
              {closeBtn}
            </Hero>
          );
        }
        if (tiles.length === 1) {
          return (
            <Hero
              $url={tiles[0].url}
              onClick={gallery.length > 1 ? openGallery : undefined}
              style={gallery.length > 1 ? { cursor: 'pointer' } : {}}
            >
              {closeBtn}
            </Hero>
          );
        }
        return (
          <HeroGrid onClick={openGallery}>
            <HeroMain $url={tiles[0].url} />
            <HeroSide>
              <HeroSmall $url={tiles[1].url} />
              {tiles[2] ? (
                <HeroSmall $url={tiles[2].url}>
                  {extra > 0 && <PlusOverlay>+{extra}</PlusOverlay>}
                </HeroSmall>
              ) : (
                <HeroSmallEmpty />
              )}
            </HeroSide>
            {closeBtn}
          </HeroGrid>
        );
      })()}

      <Body>
        <Name>{academy.name}</Name>
        <AcademyPublicInfo academy={academy} />
      </Body>

      {!isOwner && (
        <BottomBar>
          <BookmarkBtn type="button" onClick={() => setLiked((v) => !v)}>
            <IoBookmark size={20} color={liked ? '#4A7CFE' : '#6B7280'} />
            <BookmarkNum>{academy.likes || 0}</BookmarkNum>
          </BookmarkBtn>
          <ConsultBtn type="button" onClick={() => navigate(`/app/chat/${id}`)}>
            <IoCalendarOutline size={17} /> 상담신청
          </ConsultBtn>
          <CallBtn type="button" onClick={handleCall}>
            <IoCallOutline size={17} /> 전화문의
          </CallBtn>
        </BottomBar>
      )}
    </Wrap>
  );
};

export default AcademyDetailPage;

const Wrap = styled.div`
  background: #fff;
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $url }) => ($url ? `#EDEFF3 url(${$url}) center/cover` : '#EDEFF3')};
`;

const HeroGrid = styled.div`
  position: relative;
  width: 100%;
  height: 320px;
  display: flex;
  gap: 3px;
  background: #EDEFF3;
  cursor: pointer;
`;

const HeroMain = styled.div`
  flex: 2;
  height: 100%;
  background: ${({ $url }) => `#EDEFF3 url(${$url}) center/cover`};
`;

const HeroSide = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const HeroSmall = styled.div`
  flex: 1;
  position: relative;
  background: ${({ $url }) => `#EDEFF3 url(${$url}) center/cover`};
`;

const HeroSmallEmpty = styled.div`
  flex: 1;
  background: #EDEFF3;
`;

const PlusOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 20px;
  font-weight: 800;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`;

const HeroLetter = styled.span`
  font-size: 56px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.5;
`;

const Body = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 18px 16px 96px;
`;

const Name = styled.p`
  font-size: 21px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BottomBar = styled.div`
  position: fixed;
  bottom: 0;             /* 풀스크린이라 풋바 없음 — 화면 끝까지 */
  left: 0;
  right: 0;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 16px 16px;
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  z-index: 5;
`;

const BookmarkBtn = styled.button`
  flex-shrink: 0;
  width: 54px;
  height: 50px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: #fff;
`;

const BookmarkNum = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ConsultBtn = styled.button`
  flex: 1;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 14px;
  background: #EEF2FF;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14.5px;
  font-weight: 700;
`;

const CallBtn = styled.button`
  flex: 1;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
`;

const Center = styled.p`
  text-align: center;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.text.hint};
`;
